import { useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '../auth/AuthContext'
import { ApiError } from '../lib/api'
import { useT } from '../lib/i18n'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'

/** The shortest password the backend accepts (`dto.MinPasswordLength`). */
const MIN_PASSWORD = 8

type Mode = 'signIn' | 'register'

interface FieldErrors {
  email?: string
  password?: string
}

/**
 * Remove a field's error.
 *
 * `exactOptionalPropertyTypes` is on, so `{ email: undefined }` is not the same
 * type as `{}` — and it is not the same thing either: one says "no error", the
 * other says "an error whose value is undefined". Deleting the key is what the
 * Input's optional `error` prop actually means.
 */
function clearField(errors: FieldErrors, field: keyof FieldErrors): FieldErrors {
  const next = { ...errors }
  delete next[field]
  return next
}

/**
 * Sign-in and register are the same form.
 *
 * One component rather than two, because they differ in three strings and one
 * call — and two copies would be two places for the `autocomplete` attributes to
 * drift apart, which is the half a password manager depends on and nobody
 * notices is broken.
 *
 * SUAP-0202.
 */
export function AuthForm({ mode }: { mode: Mode }) {
  const { t } = useT()
  const navigate = useNavigate()
  const location = useLocation()
  const { register, signIn, signedOutReason, acknowledgeSignedOut } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fields, setFields] = useState<FieldErrors>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const from = (location.state as { from?: string } | null)?.from

  /**
   * Client-side validation, deliberately minimal.
   *
   * Enough to avoid a round trip for an empty field, and no more: the backend is
   * the authority on what it accepts, and a client rule that is stricter than
   * the server's rejects input the product would have taken.
   */
  function validate(): boolean {
    const next: FieldErrors = {}
    if (email.trim() === '') next.email = t('auth.error.email.required')
    else if (!email.includes('@')) next.email = t('auth.error.email.invalid')

    if (password === '') next.password = t('auth.error.password.required')
    else if (mode === 'register' && password.length < MIN_PASSWORD) {
      next.password = t('auth.error.password.short')
    }

    setFields(next)
    return Object.keys(next).length === 0
  }

  /**
   * Map `error.code` onto a message, and onto a field where there is one.
   *
   * Branching on the code rather than the message is SUAP-0005's rule: the
   * message is localised and may change, the code is a contract. `details`
   * carries the backend's own per-field messages, which are already localised
   * via `X-Suap-Language` — so a field the server marked wins over anything
   * decided here.
   */
  function report(error: unknown): void {
    if (!(error instanceof ApiError)) {
      setFormError(t('error.server'))
      return
    }

    const serverFields: FieldErrors = {}
    const emailDetail = error.fieldError('email')
    const passwordDetail = error.fieldError('password')
    if (emailDetail !== undefined) serverFields.email = emailDetail
    if (passwordDetail !== undefined) serverFields.password = passwordDetail

    switch (error.code) {
      case 'invalid_credentials':
        // Not attached to a field on purpose: saying *which* of the two was
        // wrong tells an attacker whether an account exists.
        setFormError(t('auth.error.invalidCredentials'))
        break
      case 'email_taken':
        serverFields.email = t('auth.error.emailTaken')
        break
      case 'rate_limited':
        setFormError(t('auth.error.rateLimited'))
        break
      case 'validation_failed':
        // The details are the message; a second sentence above them would be
        // repeating the same thing less precisely.
        if (Object.keys(serverFields).length === 0) setFormError(error.message)
        break
      case 'network_unavailable':
        setFormError(t('error.offline'))
        break
      default:
        setFormError(error.message)
    }

    setFields(serverFields)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    setFormError(null)
    if (!validate()) return

    setBusy(true)
    try {
      if (mode === 'register') await register(email.trim(), password)
      else await signIn(email.trim(), password)
      navigate(from ?? '/today', { replace: true })
    } catch (error) {
      report(error)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
      {signedOutReason === 'revoked' ? (
        <p
          className="rounded-card border border-hairline bg-surface p-3 text-sm text-ink"
          role="status"
          // Read once, when it appears. It is information, not an error: the
          // user did nothing wrong and there is nothing to fix but signing in.
          aria-live="polite"
        >
          {t('auth.signedOut.revoked')}
        </p>
      ) : null}

      <Input
        label={t('auth.email')}
        type="email"
        inputMode="email"
        // The half a password manager needs. `username` rather than `email` is
        // what browsers actually key on for the credential pair.
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        value={email}
        onChange={(e) => {
          setEmail(e.target.value)
          if (fields.email !== undefined) setFields((f) => clearField(f, 'email'))
          if (signedOutReason !== null) acknowledgeSignedOut()
        }}
        {...(fields.email === undefined ? {} : { error: fields.email })}
      />

      <Input
        label={t('auth.password')}
        type="password"
        // `new-password` on register is what makes a manager *offer to generate*
        // one; `current-password` on sign-in is what makes it fill. Getting these
        // the wrong way round silently degrades both.
        autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
        value={password}
        onChange={(e) => {
          setPassword(e.target.value)
          if (fields.password !== undefined) setFields((f) => clearField(f, 'password'))
        }}
        {...(mode === 'register' ? { hint: t('auth.password.hint') } : {})}
        {...(fields.password === undefined ? {} : { error: fields.password })}
      />

      {formError === null ? null : (
        <p className="text-sm text-over" role="alert">
          {formError}
        </p>
      )}

      <Button type="submit" block loading={busy}>
        {mode === 'register' ? t('auth.register.action') : t('auth.signIn.action')}
      </Button>

      {/* The switch between the two modes.
       *
       * `min-h-11` and the padding are not decoration: as a bare inline link this
       * measured **42×18** and `check:a11y` failed it on both themes. An
       * inline-flex link inside the sentence keeps the copy reading as a sentence
       * while giving the tap a real target.
       *
       * The focus ring is the system's, not the browser's. The default
       * `outline: 1px auto` measured 5.26:1 against the light ground and failed
       * the same check — `outline-ink` is what every other focusable thing in the
       * app uses (SUAP-0113). */}
      <p className="flex flex-wrap items-center justify-center gap-1 text-center text-sm text-ink-soft">
        {mode === 'register' ? t('auth.register.switch') : t('auth.signIn.switch')}
        <Link
          to={mode === 'register' ? '/signin' : '/register'}
          state={from === undefined ? undefined : { from }}
          className={[
            'inline-flex min-h-11 items-center rounded-input px-2',
            'font-medium text-accent-fg underline underline-offset-2',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink',
          ].join(' ')}
        >
          {mode === 'register' ? t('auth.register.switchAction') : t('auth.signIn.switchAction')}
        </Link>
      </p>
    </form>
  )
}
